/**
 * NotificationCenter — renders the floating notification stack.
 * Positioned at top-right of the viewport.
 * Manages visible notifications with virtualized rendering for performance.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Notification } from "../../services/notification.types";
import { DEFAULT_CONFIG } from "../../services/notification.types";
import NotificationItem from "./NotificationItem";

interface NotificationCenterProps {
 notifications: Notification[];
 onDismiss: (id: string) => void;
}

export default function NotificationCenter({ notifications, onDismiss }: NotificationCenterProps) {
 const navigate = useNavigate();

 // Only show the most recent N notifications
 const visibleNotifications = notifications
 .filter((n) => !n.isDismissed)
 .slice(0, DEFAULT_CONFIG.maxVisible);

 const handleAction = useCallback(
 (url: string) => {
 if (url.startsWith("http")) {
 window.open(url,"_blank","noopener,noreferrer");
 } else {
 navigate(url);
 }
 },
 [navigate]
 );

 if (visibleNotifications.length === 0) return null;

 return (
 <div
 aria-label="Notifications"aria-live="polite"aria-relevant="additions removals"role="region"className="fixed top-16 right-4 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] pointer-events-none">
 <div className="flex flex-col pointer-events-auto">
 {visibleNotifications.map((notification) => (
 <NotificationItem
 key={notification.id}
 notification={notification}
 onDismiss={onDismiss}
 onAction={handleAction}
 />
 ))}
 </div>

 {/* Overflow indicator */}
 {notifications.filter((n) => !n.isDismissed).length > DEFAULT_CONFIG.maxVisible && (
 <div className="pointer-events-auto mt-1 text-center">
 <span className="inline-block rounded-full bg-slate-900/70 px-3 py-1 text-[10px] font-medium text-white backdrop-blur-sm">
 +{notifications.filter((n) => !n.isDismissed).length - DEFAULT_CONFIG.maxVisible} more
 </span>
 </div>
 )}
 </div>
 );
}
